import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  alpha,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Link,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { LineChart, BarChart } from '@mui/x-charts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SavingsIcon from '@mui/icons-material/Savings';
import StoreIcon from '@mui/icons-material/Store';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getSpendingSummary } from '@/api/reports';
import { useReceiptListStore } from '@/store/receiptListStore';
import type { GetSpendingSummaryResponse } from '../../generated/reports/v1/reports_pb';

const formatCurrency = (value: number) => `$${value.toFixed(2)}`;

// Convert string to title case (e.g., "VISA" -> "Visa", "master card" -> "Master Card")
const toTitleCase = (str: string) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Professional color palette
const chartColors = {
  primary: '#2563EB', // Theme Primary (Blue 600)
  secondary: '#10B981',
  accent: '#8B5CF6',
  warning: '#F59E0B',
  danger: '#EF4444',
  neutral: '#6B7280',
  pieColors: ['#2563EB', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#6B7280'],
};

interface KpiCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: { value: number; isPositive: boolean };
  color: string;
}

function KpiCard({ title, value, subtitle, icon, trend, color }: KpiCardProps) {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: `0 4px 20px ${alpha(color, 0.15)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.75rem',
              }}
            >
              {title}
            </Typography>
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: alpha(color, 0.1),
                color: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {value}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {trend && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  color: trend.isPositive ? 'success.main' : 'error.main',
                }}
              >
                {trend.isPositive ? (
                  <TrendingDownIcon sx={{ fontSize: 16 }} />
                ) : (
                  <TrendingUpIcon sx={{ fontSize: 16 }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {trend.value >= 0 ? '+' : ''}
                  {trend.value.toFixed(1)}%
                </Typography>
              </Stack>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

interface RingChartDatum {
  id: string | number;
  value: number;
  label: string;
  color: string;
}

function RingChart({ data, width = 300, height = 300 }: { data: RingChartDatum[]; width?: number; height?: number }) {
  const safeData = data.filter((d) => Number.isFinite(d.value) && d.value > 0);
  const total = safeData.reduce((sum, d) => sum + d.value, 0);
  const cx = width / 2;
  const cy = height / 2;
  const outerRadius = Math.min(width, height) * 0.44;
  const innerRadius = outerRadius * 0.62;

  const polarToCartesian = (radius: number, angleRad: number) => ({
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  });

  const buildSlicePath = (startAngle: number, endAngle: number) => {
    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;
    const outerStart = polarToCartesian(outerRadius, startAngle);
    const outerEnd = polarToCartesian(outerRadius, endAngle);
    const innerStart = polarToCartesian(innerRadius, startAngle);
    const innerEnd = polarToCartesian(innerRadius, endAngle);

    return [
      `M ${outerStart.x} ${outerStart.y}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}`,
      `L ${innerEnd.x} ${innerEnd.y}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
      'Z',
    ].join(' ');
  };

  if (total <= 0) {
    return (
      <Box sx={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No chart data
        </Typography>
      </Box>
    );
  }

  let cursor = -Math.PI / 2;
  const gap = 0.025;
  const segments = safeData.map((d) => {
    const angle = (d.value / total) * Math.PI * 2;
    const start = cursor;
    const end = cursor + Math.max(0, angle - gap);
    cursor += angle;

    return {
      id: d.id,
      color: d.color,
      path: buildSlicePath(start, end),
      label: d.label,
      value: d.value,
    };
  });

  return (
    <Box sx={{ width, height }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Ring chart">
        {segments.map((segment) => (
          <path key={segment.id} d={segment.path} fill={segment.color}>
            <title>{`${segment.label}: ${formatCurrency(segment.value)}`}</title>
          </path>
        ))}
      </svg>
    </Box>
  );
}

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function ReportsPage() {
  const theme = useTheme();
  const [summary, setSummary] = useState<GetSpendingSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setShowOnlyUnreconciled = useReceiptListStore((state) => state.setShowOnlyUnreconciled);

  // Month selection state - default to current month
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  // Build available months - show all months from current month back to oldest receipt data
  // or at least 12 months if no data yet
  const availableMonths = useMemo(() => {
    const months: { year: number; month: number; label: string }[] = [];

    // Find the oldest month from data, or default to 12 months ago
    let oldestYear = now.getFullYear();
    let oldestMonth = now.getMonth() + 1;

    if (summary?.monthlySpending?.length) {
      for (const m of summary.monthlySpending) {
        if (m.year < oldestYear || (m.year === oldestYear && m.month < oldestMonth)) {
          oldestYear = m.year;
          oldestMonth = m.month;
        }
      }
    } else {
      // Default to showing 12 months back
      const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      oldestYear = twelveMonthsAgo.getFullYear();
      oldestMonth = twelveMonthsAgo.getMonth() + 1;
    }

    // Generate all months from current month back to oldest
    let year = now.getFullYear();
    let month = now.getMonth() + 1;

    while (year > oldestYear || (year === oldestYear && month >= oldestMonth)) {
      months.push({
        year,
        month,
        label: `${monthNames[month - 1]} ${year}`,
      });

      month--;
      if (month < 1) {
        month = 12;
        year--;
      }
    }

    return months;
  }, [summary?.monthlySpending, now]);

  // Comparison months state (2, 3, or 6 months)
  const [comparisonMonths, setComparisonMonths] = useState(2);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getSpendingSummary(selectedYear, selectedMonth);
        setSummary(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load reports');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, [selectedYear, selectedMonth]);

  const handleReviewClick = () => {
    setShowOnlyUnreconciled(true);
  };

  const {
    currentMonthTotal,
    currentMonthLabel,
    changeAmount,
    changePercent,
    dailyTotals,
    daysInMonth,
    storeChartData,
    storeStats,
    totalReceipts,
    receiptsWithDates,
    uniqueStores,
    paymentStats,
    paymentChartData,
    avgPerMonth,
    comparisonData,
    totalDiscounts,
  } = useMemo(() => {
    if (!summary) {
      return {
        currentMonthTotal: 0,
        currentMonthLabel: '',
        changeAmount: 0,
        changePercent: null as number | null,
        dailyTotals: [] as number[],
        daysInMonth: 31,
        storeChartData: [],
        storeStats: [],
        totalReceipts: 0,
        receiptsWithDates: 0,
        uniqueStores: 0,
        paymentStats: [] as { paymentMethod: string; total: number; receiptCount: number }[],
        paymentChartData: [] as { id: string; value: number; label: string; color: string }[],
        avgPerMonth: 0,
        comparisonData: [] as { label: string; total: number }[],
        totalDiscounts: 0,
      };
    }

    const current = summary.currentPeriod;
    const previous = summary.previousPeriod;
    const overall = summary.overallStats;

    const currentTotal = current?.total ?? 0;
    const lastTotal = previous?.total ?? 0;
    const change = currentTotal - lastTotal;
    const percent = lastTotal > 0 ? (change / lastTotal) * 100 : null;

    const currentMonth = current?.month ?? new Date().getMonth() + 1;
    const currentYear = current?.year ?? new Date().getFullYear();

    // Build comparison data relative to the selected month
    const monthlyData = summary.monthlySpending ?? [];
    const selectedIdx = monthlyData.findIndex(
      (m) => m.year === currentYear && m.month === currentMonth
    );
    const startIdx = selectedIdx >= 0 ? selectedIdx : 0;
    const comparison = monthlyData.slice(startIdx, startIdx + comparisonMonths).map((m) => ({
      label: `${monthNames[(m.month - 1) % 12].slice(0, 3)} ${m.year}`,
      total: m.total,
    })).reverse(); // Reverse to show oldest first

    // Calculate days in current month
    const days = new Date(currentYear, currentMonth, 0).getDate();

    // Build daily totals array (fill with 0s, then populate from API data)
    const dailyArr = Array.from({ length: days }, () => 0);
    for (const day of summary.dailySpending ?? []) {
      if (day.day >= 1 && day.day <= days) {
        dailyArr[day.day - 1] = day.total;
      }
    }

    // Build store chart data
    const stores = summary.storeSpending ?? [];
    const topStores = stores.slice(0, 6);
    const otherTotal = stores.slice(6).reduce((sum, s) => sum + s.total, 0);

    const chartData = topStores.map((store, index) => ({
      id: store.store === 'N/A' ? `unknown-${index}` : store.store,
      value: store.total,
      label: store.store === 'N/A' ? 'Unknown' : toTitleCase(store.store),
      color: chartColors.pieColors[index % chartColors.pieColors.length],
    }));

    if (otherTotal > 0) {
      chartData.push({
        id: 'other',
        value: Number(otherTotal),
        label: 'Other',
        color: chartColors.neutral,
      });
    }

    // Build payment method chart data
    const payments = summary.paymentMethodSpending ?? [];
    const paymentChart = payments.map((pm, index) => ({
      id: index,
      value: Number(pm.total),
      label: pm.paymentMethod ? toTitleCase(pm.paymentMethod) : 'Unknown',
      color: chartColors.pieColors[index % chartColors.pieColors.length],
    }));

    return {
      currentMonthTotal: currentTotal,
      currentMonthLabel: monthNames[(currentMonth - 1) % 12],
      changeAmount: change,
      changePercent: percent,
      dailyTotals: dailyArr,
      daysInMonth: days,
      storeChartData: chartData,
      storeStats: stores,
      totalReceipts: overall?.totalReceipts ?? 0,
      receiptsWithDates: overall?.receiptsWithDates ?? 0,
      uniqueStores: overall?.uniqueStores ?? 0,
      paymentStats: payments,
      paymentChartData: paymentChart,
      avgPerMonth: overall?.averagePerMonth ?? 0,
      comparisonData: comparison,
      totalDiscounts: overall?.totalDiscounts ?? 0,
    };
  }, [summary, comparisonMonths]);

  const dayLabels = Array.from({ length: daysInMonth }, (_, index) => index + 1);

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto', overflowX: 'hidden' }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track your spending trends and store-level insights
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Month</InputLabel>
          <Select
            id="month-select"
            value={`${selectedYear}-${selectedMonth}`}
            label="Month"
            MenuProps={{ disableRestoreFocus: true }}
            onChange={(e) => {
              const [year, month] = e.target.value.split('-').map(Number);
              setSelectedYear(year);
              setSelectedMonth(month);
            }}
          >
            {availableMonths.map((m) => (
              <MenuItem key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {(summary?.overallStats?.unreconciledCount ?? 0) > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <AlertTitle>Receipts Need Review</AlertTitle>
          {summary?.overallStats?.unreconciledCount} receipt{(summary?.overallStats?.unreconciledCount ?? 0) !== 1 ? 's' : ''} ha{(summary?.overallStats?.unreconciledCount ?? 0) !== 1 ? 've' : 's'} totals that don't match - data will not be included in calculations until this is resolved.{' '}
          <Link
            component={RouterLink}
            to="/receipts"
            onClick={handleReviewClick}
            sx={{ fontWeight: 600 }}
          >
            Review now
          </Link>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : !summary || totalReceipts === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No receipts found yet. Upload a receipt to get started.
        </Alert>
      ) : (
        <Stack spacing={3}>
          {/* KPI Cards */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiCard
                title="This Month"
                value={formatCurrency(currentMonthTotal)}
                subtitle={currentMonthLabel}
                icon={<CalendarTodayIcon sx={{ fontSize: 20 }} />}
                trend={changePercent !== null ? { value: changePercent, isPositive: changePercent < 0 } : undefined}
                color={chartColors.primary}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiCard
                title="Avg per Month"
                value={formatCurrency(avgPerMonth)}
                subtitle="All time"
                icon={<AccountBalanceWalletIcon sx={{ fontSize: 20 }} />}
                color={chartColors.accent}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiCard
                title="Total Savings"
                value={formatCurrency(totalDiscounts)}
                subtitle="All time discounts"
                icon={<SavingsIcon sx={{ fontSize: 20 }} />}
                color={chartColors.secondary}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KpiCard
                title="Stores Visited"
                value={uniqueStores.toString()}
                subtitle="Unique stores"
                icon={<StoreIcon sx={{ fontSize: 20 }} />}
                color={chartColors.warning}
              />
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Daily Spending
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentMonthLabel} spending breakdown by day
                      </Typography>
                    </Box>
                    {receiptsWithDates > 0 ? (
                      <LineChart
                        height={280}
                        xAxis={[
                          {
                            data: dayLabels,
                            label: 'Day of Month',
                            scaleType: 'band',
                          },
                        ]}
                        series={[
                          {
                            data: dailyTotals,
                            label: 'Spend',
                            color: chartColors.primary,
                            valueFormatter: (value) => formatCurrency(value ?? 0),
                            area: true,
                          },
                        ]}
                        margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
                        sx={{
                          '.MuiLineElement-root': {
                            strokeWidth: 2,
                          },
                          '.MuiAreaElement-root': {
                            fillOpacity: 0.1,
                          },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 280,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Add receipts with dates to see the daily trend
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Month Comparison
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last {comparisonMonths} months
                        </Typography>
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          id="comparison-months"
                          value={comparisonMonths}
                          onChange={(e) => setComparisonMonths(Number(e.target.value))}
                          MenuProps={{ disableRestoreFocus: true }}
                        >
                          <MenuItem value={2}>2 months</MenuItem>
                          <MenuItem value={3}>3 months</MenuItem>
                          <MenuItem value={6}>6 months</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <BarChart
                      height={240}
                      xAxis={[
                        {
                          data: comparisonData.map((d) => d.label),
                          scaleType: 'band',
                        },
                      ]}
                      series={[
                        {
                          data: comparisonData.map((d) => d.total),
                          label: 'Monthly Spend',
                          color: chartColors.secondary,
                          valueFormatter: (value) => formatCurrency(value ?? 0),
                        },
                      ]}
                      margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
                      sx={{
                        '.MuiBarElement-root': {
                          rx: 4,
                        },
                      }}
                    />
                    <Divider />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {comparisonData.length > 0 ? comparisonData[0].label : ''}: {formatCurrency(comparisonData[0]?.total ?? 0)}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {changeAmount <= 0 ? (
                          <TrendingDownIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        ) : (
                          <TrendingUpIcon sx={{ fontSize: 18, color: 'error.main' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: changeAmount <= 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {changeAmount >= 0 ? '+' : ''}
                          {formatCurrency(changeAmount)}
                          {changePercent !== null && ` (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(1)}%)`}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Spend by Store Section */}
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Spending by Store
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Spending breakdown for {monthNames[selectedMonth - 1]} {selectedYear}
                  </Typography>
                </Box>

                {storeStats.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No store data available yet
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <RingChart data={storeChartData} />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                              }}
                            >
                              <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Store</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, py: 1.5 }}>
                                Receipts
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                                Total
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {storeStats.map((store, index) => (
                              <TableRow
                                key={store.store}
                                sx={{
                                  '&:nth-of-type(even)': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                                  },
                                  '&:last-child td': { border: 0 },
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                  },
                                }}
                              >
                                <TableCell sx={{ py: 1.5 }}>
                                  <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor:
                                          index < chartColors.pieColors.length
                                            ? chartColors.pieColors[index]
                                            : chartColors.neutral,
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {store.store === 'N/A' ? 'Unknown' : toTitleCase(store.store)}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    {store.receiptCount}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(store.total)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Spend by Payment Method Section */}
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Spending by Payment Method
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Spending breakdown for {monthNames[selectedMonth - 1]} {selectedYear}
                  </Typography>
                </Box>

                {paymentStats.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No payment data available yet
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <RingChart data={paymentChartData} />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                              }}
                            >
                              <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Payment Method</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, py: 1.5 }}>
                                Receipts
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>
                                Total
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paymentStats.map((pm, index) => (
                              <TableRow
                                key={pm.paymentMethod || `unknown-${index}`}
                                sx={{
                                  '&:nth-of-type(even)': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                                  },
                                  '&:last-child td': { border: 0 },
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                  },
                                }}
                              >
                                <TableCell sx={{ py: 1.5 }}>
                                  <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor:
                                          index < chartColors.pieColors.length
                                            ? chartColors.pieColors[index]
                                            : chartColors.neutral,
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {pm.paymentMethod ? toTitleCase(pm.paymentMethod) : 'Unknown'}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    {pm.receiptCount}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(pm.total)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
}
